import { colors } from '@globals/themes/defaultTheme'
import { onActive, onDisabled, onError, onFocus, onHover } from '@globals/themes/themeConstants'
import Box from '@mui/material/Box'
import MuiTextAreaAutosize from '@mui/material/TextareaAutosize'
import Typography from '@mui/material/Typography'
import { SxProps, styled } from '@mui/material/styles'
import { T } from '@transifex/react'
import { ReactNode } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import ErrorText from './ErrorText'
import FieldLabel from './FieldLabel'
import HelpText from './HelpText'

const MAX_CHARS = 150

export const MuiTextAreaStyled = styled(MuiTextAreaAutosize)(({ theme }) => ({
  backgroundColor: colors.white,
  border: `1px solid ${colors.grayExtraLight}`,
  borderRadius: theme.spacing(1),
  boxSizing: 'border-box',
  font: 'inherit',
  padding: '8px 16px',
  width: '100%',
  minHeight: 0,
  maxHeight: 400,
  overflow: 'auto',
  resize: 'vertical',
  [onHover]: {
    border: `1px solid ${colors.blueMedium}`,
  },
  [onFocus]: {
    outline: 'none',
  },
  [onActive]: {
    border: `1px solid ${colors.bluePrimary}`,
  },
  [onDisabled]: {
    border: `1px solid ${colors.grayExtraLight}`,
  },
  [onError]: {
    border: `1px solid ${colors.redPrimary}`,
  },
  '&::placeholder': {
    color: colors.grayMedium,
    opacity: 1,
  },
}))

export type TextAreaFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label: ReactNode
  placeholder?: string
  required?: boolean
  help?: string
  disabled?: boolean
  onBlur?: () => void
  labelStyle?: SxProps
  maxChars?: number
  hideCounter?: boolean
  minRows?: number
}

const TextAreaField = <T extends FieldValues>({
  control,
  label,
  name,
  help,
  placeholder,
  required = true,
  disabled = false,
  onBlur,
  labelStyle,
  maxChars = MAX_CHARS,
  hideCounter = false,
  minRows = 3,
}: TextAreaFieldProps<T>) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange, value, onBlur: onControllerBlur, ref },
          fieldState: { error },
        }) => (
          <>
            <FieldLabel
              htmlFor={name}
              required={required}
              disabled={disabled}
              labelStyle={labelStyle}>
              {label}
            </FieldLabel>
            {help && <HelpText disabled={disabled}>{help}</HelpText>}
            <MuiTextAreaStyled
              onBlur={() => {
                onBlur && onBlur()
                onControllerBlur()
              }}
              placeholder={placeholder}
              disabled={disabled}
              minRows={minRows}
              ref={ref}
              id={name}
              value={value || ''}
              onChange={e => {
                onChange(e)
              }}
              name={name}
              sx={error ? { border: `1px solid ${colors.redPrimary}` } : undefined}
            />
            {!hideCounter && (
              <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <T
                  _str="{count}/{maxChars}"
                  count={value?.length.toLocaleString() || 0}
                  maxChars={maxChars.toLocaleString()}
                />
              </Typography>
            )}
            {error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Box>
  )
}

export default TextAreaField
